import { useState, useMemo } from "react";
import { Eye, Ban, RefreshCw, Download, Flag, SlidersHorizontal } from "lucide-react";
import type { AdminTalent } from "../../data";
import { useAdminData } from "../../hooks/useAdminData";
import { talents as talentsApi } from "../../api/endpoints";
import { downloadCsv } from "../../exportCsv";
import { AddToast } from "../../components/feedback/Toast";
import { Avatar } from "../../components/ui/Avatar";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { FilterDrawer, FilterFieldDef } from "../../components/ui/FilterDrawer";
import { PageHeader } from "../../components/ui/PageHeader";
import { SearchBar } from "../../components/ui/SearchBar";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { CORAL, F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";

// ── Talents list ─────────────────────────────────────────────────────────────

export function TalentsView({ onDetail, addToast }: { onDetail: (t: AdminTalent) => void; addToast: AddToast }) {
  const { ADMIN_TALENTS, PLATFORM_STATS, refetchTalents } = useAdminData();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [verifFilter, setVerifFilter] = useState("All");
  const [selected, setSelected] = useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const talentFilterFields: FilterFieldDef[] = [
    { type: "chips", key: "verification", label: "Verification", options: ["All", "Verified", "Pending", "Unverified", "Rejected"] },
    { type: "chips", key: "status", label: "Status", options: ["All", "Active", "Suspended", "Archived"] },
  ];
  const hasFilter = statusFilter !== "All" || verifFilter !== "All";

  const filtered = useMemo(() => ADMIN_TALENTS.filter((t) => {
    const s = search.toLowerCase();
    const matchSearch = !s || t.name.toLowerCase().includes(s) || t.category.toLowerCase().includes(s) || t.location.toLowerCase().includes(s);
    const matchStatus = statusFilter === "All" || t.account === statusFilter;
    const matchVerif = verifFilter === "All" || t.verification === verifFilter;
    return matchSearch && matchStatus && matchVerif;
  }), [ADMIN_TALENTS, search, statusFilter, verifFilter]);

  function setAccountStatus(id: string, name: string, status: "Active" | "Suspended") {
    talentsApi.update(id, { account_status: status })
      .then(() => { refetchTalents(); addToast(`${name} ${status === "Suspended" ? "suspended" : "reactivated"}`, status === "Suspended" ? "error" : "success"); })
      .catch(() => addToast("Failed to update account status", "error"));
  }

  // Bulk suspend is one request per account — there is no batch endpoint, and
  // inventing one to hide N failures behind a single toast would be worse.
  function suspendSelected() {
    const targets = ADMIN_TALENTS.filter((t) => selected.includes(t.id));
    if (!window.confirm(`Suspend ${targets.length} talent account${targets.length === 1 ? "" : "s"}? They are signed out immediately.`)) return;

    Promise.all(
      targets.map((t) =>
        talentsApi.update(t.id, { account_status: "Suspended" }).then(() => true).catch(() => false)
      )
    ).then((results) => {
      const ok = results.filter(Boolean).length;
      const failed = results.length - ok;
      refetchTalents();
      setSelected([]);
      addToast(failed === 0 ? `${ok} talent suspended` : `${ok} suspended, ${failed} failed`, failed === 0 ? "error" : "info");
    });
  }

  function exportTalents(rows: AdminTalent[]) {
    const count = downloadCsv("donatella-talent", rows, [
      { header: "Name", value: (t) => t.name },
      { header: "Email", value: (t) => t.email },
      { header: "Category", value: (t) => t.category },
      { header: "Location", value: (t) => t.location },
      { header: "Verification", value: (t) => t.verification },
      { header: "Account", value: (t) => t.account },
      { header: "Joined", value: (t) => t.joinedDate },
      { header: "Last active", value: (t) => t.lastActive },
      { header: "Applications", value: (t) => t.totalApplications },
      { header: "Hires", value: (t) => t.totalHires },
      { header: "Earnings", value: (t) => t.totalEarnings },
      { header: "Wallet", value: (t) => t.walletBalance },
      { header: "In escrow", value: (t) => t.pendingPayout },
      { header: "Rating", value: (t) => t.rating },
    ]);
    setSelected([]);
    addToast(`Exported ${count} talent`, "success");
  }

  const allSelected = filtered.length > 0 && filtered.every((t) => selected.includes(t.id));

  return (
    <div>
      <FilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Talent" fields={talentFilterFields}
        values={{ verification: verifFilter, status: statusFilter }}
        onApply={(v) => { setVerifFilter(v.verification ?? "All"); setStatusFilter(v.status ?? "All"); }} />

      <PageHeader title="Talent" sub={`${PLATFORM_STATS.totalTalents.toLocaleString()} registered talent`} actions={
        selected.length > 0 ? (
          <>
            <Btn variant="ghost" onClick={suspendSelected}><Ban size={13} /> Suspend {selected.length}</Btn>
            <Btn variant="ghost" onClick={() => exportTalents(ADMIN_TALENTS.filter((t) => selected.includes(t.id)))}><Download size={13} /> Export</Btn>
          </>
        ) : undefined
      } />

      <div style={{ display: "flex", gap: "10px", marginBottom: "18px", flexWrap: "wrap", alignItems: "center" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search talent…" />
        <button className="filter-btn" onClick={() => setDrawerOpen(true)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 13px", borderRadius: "8px", fontSize: "12px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${hasFilter ? CORAL : T.border}`, background: hasFilter ? `rgba(209,0,75,0.06)` : T.s2, color: hasFilter ? CORAL : T.sub, transition: "all 0.15s" }}>
          <SlidersHorizontal size={13} /> Filters
        </button>
        {hasFilter && (
          <button onClick={() => { setStatusFilter("All"); setVerifFilter("All"); }} style={{ fontSize: "11px", color: CORAL, fontFamily: F, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Clear</button>
        )}
      </div>

      <Card>
        <TableWrap>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F, minWidth: "960px" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                <th style={{ padding: "10px 14px", textAlign: "left", width: "36px" }}>
                  <input type="checkbox" checked={allSelected} onChange={() => allSelected ? setSelected([]) : setSelected(filtered.map((t) => t.id))} style={{ cursor: "pointer" }} />
                </th>
                {["Talent", "Category", "Day rate", "Wallet", "Pending", "Verification", "Status", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} style={{ borderBottom: `1px solid ${T.border}`, transition: "background 0.1s" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.s2; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}>
                  <td style={{ padding: "12px 14px" }}>
                    <input type="checkbox" checked={selected.includes(t.id)} onChange={() => setSelected((p) => p.includes(t.id) ? p.filter((x) => x !== t.id) : [...p, t.id])} style={{ cursor: "pointer" }} />
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Avatar initials={t.initials} color={t.color} size={32} />
                      <div>
                        <p style={{ fontSize: "12px", fontWeight: 600, color: T.text }}>{t.name}</p>
                        <p style={{ fontSize: "10px", color: T.sub }}>{t.location}</p>
                      </div>
                      {t.flagged && <Flag size={11} style={{ color: "#991b1b" }} />}
                    </div>
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: "12px", color: T.sub }}>{t.category}</td>
                  <td style={{ padding: "12px 14px", fontSize: "12px", fontWeight: 600, color: T.text }}>{t.dayRate}</td>
                  <td style={{ padding: "12px 14px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 600, color: t.walletBalance === "SAR 0" ? T.muted : "#166534", fontFamily: F }}>{t.walletBalance}</p>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <p style={{ fontSize: "11px", color: t.pendingPayout === "SAR 0" ? T.muted : "#92400e", fontFamily: F }}>{t.pendingPayout}</p>
                  </td>
                  <td style={{ padding: "12px 14px" }}><StatusBadge status={t.verification} /></td>
                  <td style={{ padding: "12px 14px" }}><StatusBadge status={t.account} /></td>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <Btn variant="ghost" size="xs" onClick={() => onDetail(t)}><Eye size={11} /> View</Btn>
                      {t.account === "Active" && <Btn variant="ghost" size="xs" onClick={() => setAccountStatus(t.id, t.name, "Suspended")}><Ban size={11} /></Btn>}
                      {t.account === "Suspended" && <Btn variant="ghost" size="xs" onClick={() => setAccountStatus(t.id, t.name, "Active")}><RefreshCw size={11} /></Btn>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
          {filtered.length === 0 && <div style={{ padding: "48px", textAlign: "center", color: T.sub, fontSize: "13px", fontFamily: F }}>No talent matching current filters.</div>}
      </Card>
    </div>
  );
}
